import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InsightFaqDialog from '../../../../faqSection/InsightFaqDialog';
import { OPEN_FAQ_DIALOG_FROM_SEARCH, OPEN_KNOWLEDGE_BASE_FROM_SEARCH, START_GUIDED_TOUR_FROM_SEARCH } from '../SearchSection/searchEvents';
// project imports
import { ThemeMode } from 'config';

import Transitions from 'ui-component/extended/Transitions';
import { IconHelp, IconHelpHexagon, IconClipboardText, IconBulb } from '@tabler/icons-react';
import { FormattedMessage } from 'react-intl';

const KNOWLEDGE_BASE_URL = 'https://www.netgear.com/support/product/insight';

// ==============================|| LOCALIZATION ||============================== //

export default function SupportSection({ startTour }) {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const [faqOpen, setFaqOpen] = useState(false);

  const SUPPORTITEMS = [
    { labelId: 'supportMenuFaq', icon: IconHelpHexagon, code: 'faq' },
    {
      labelId: 'supportMenuInsightKnowledgeBase',
      icon: IconClipboardText,
      code: 'kb',
      url: KNOWLEDGE_BASE_URL
    },
    { labelId: 'startGuidedTour', icon: IconBulb, code: 'tour' }
  ];

  // Global search results for the Help menu items do what the matching menu item does.
  useEffect(() => {
    const onOpenFaq = () => {
      setOpen(false);
      setFaqOpen(true);
    };
    const onOpenKnowledgeBase = () => {
      window.open(KNOWLEDGE_BASE_URL, '_blank', 'noopener,noreferrer');
      setOpen(false);
    };
    const onStartTour = () => {
      startTour?.();
      setOpen(false);
    };
    window.addEventListener(OPEN_FAQ_DIALOG_FROM_SEARCH, onOpenFaq);
    window.addEventListener(OPEN_KNOWLEDGE_BASE_FROM_SEARCH, onOpenKnowledgeBase);
    window.addEventListener(START_GUIDED_TOUR_FROM_SEARCH, onStartTour);
    return () => {
      window.removeEventListener(OPEN_FAQ_DIALOG_FROM_SEARCH, onOpenFaq);
      window.removeEventListener(OPEN_KNOWLEDGE_BASE_FROM_SEARCH, onOpenKnowledgeBase);
      window.removeEventListener(START_GUIDED_TOUR_FROM_SEARCH, onStartTour);
    };
  }, [startTour]);

  // handlers
  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  const handleListItemClick = (event, support) => {
    // External link
    if (support.url) {
      window.open(support.url, '_blank', 'noopener,noreferrer');
      setOpen(false);
      return;
    }

    // Guided tour
    if (support.code === 'tour') {
      startTour?.();
      setOpen(false);
      return;
    }

    // FAQ
    if (support.code === 'faq') {
      setOpen(false);
      setFaqOpen(true);
      return;
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <IconButton
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          color: 'white.paper',
          padding: 1,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <IconHelp stroke={1.25} size="24px" />
      </IconButton>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={downMD ? 'bottom' : 'bottom'}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 10] }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  // borderRadius: `${borderRadius}px`,
                  border: '1px solid',
                  borderColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.divider : 'transparent',
                  boxShadow: '0 3px 14px 2px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.14), 0 5px 5px -3px rgba(0,0,0,0.20)'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    left: '60%',
                    width: 15,
                    height: 15,
                    backgroundColor: 'background.paper',
                    transform: 'rotate(45deg)',
                    zIndex: -1,
                    borderTop: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    marginLeft: '-8px'
                  }}
                />
                <List sx={{ minWidth: 220 }}>
                  {SUPPORTITEMS.map((support, index) => {
                    const Icon = support.icon;

                    return (
                      <Box key={support.code}>
                        {index !== 0 && ( // 👈 skip divider for first item
                          <Divider
                            sx={{
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[150]
                            }}
                          />
                        )}

                        <ListItemButton
                          onClick={(e) => handleListItemClick(e, support)}
                          sx={{
                            py: 0.7,
                            '&:hover': {
                              backgroundColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.dark[700] : theme.palette.grey[200]
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon stroke={1.5} size="20px" />
                                <Typography variant="body1" color="grey.800">
                                  <FormattedMessage id={support.labelId} defaultMessage={support.labelId} />
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </List>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
      {/* FAQ Dialog */}
      <InsightFaqDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
    </>
  );
}
